import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Lock, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Panel, StatusPill, EmptyState, Loading, SectionLabel,
} from "@/components/app/primitives";
import { moneyFull, pct, shortDay, EMPTY_MARK } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ROLE_LABELS = { owner: "Owner", founder: "Founder", operating_partner: "Operating partner", advisor: "Advisor" };
const ROLE_OPTIONS = ["owner", "founder", "operating_partner", "advisor"];
const ARMS = ["Technology and Data", "Investments and Capital", "Venture Building", "Media and Community"];
const PASS_STATUSES = ["Active", "In process", "Expiring", "Expired", "Not required"];

function th(children, extra = "") {
  return <th className={`px-4 py-2.5 font-medium ${extra}`}>{children}</th>;
}

// ---- Directory ---------------------------------------------------------------
function PersonDialog({ person, onSaved, trigger }) {
  const editing = !!person;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(person || {
    name: "", role_title: "", role: "operating_partner", email: "", location: "", phone: "", arm: ARMS[0], equity_pct: 0,
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...f, equity_pct: parseFloat(f.equity_pct) || 0 };
      if (editing) await api.patch(`/people/${person.id}`, payload);
      else await api.post("/people", payload);
      toast.success(editing ? "Member updated" : "Member added");
      setOpen(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-lg">{editing ? "Edit member" : "Add member"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Name</Label>
              <Input data-testid="person-name-input" value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Title</Label>
              <Input value={f.role_title} onChange={(e) => set("role_title", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Studio role</Label>
              <Select value={f.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Location</Label>
              <Input value={f.location} onChange={(e) => set("location", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Email</Label>
              <Input value={f.email} onChange={(e) => set("email", e.target.value)} className="font-mono" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Phone</Label>
              <Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="font-mono" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Arm</Label>
              <Select value={f.arm || ARMS[0]} onValueChange={(v) => set("arm", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ARMS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Equity percent</Label>
              <Input type="number" value={f.equity_pct ?? 0} onChange={(e) => set("equity_pct", e.target.value)} className="font-mono" /></div>
          </div>
        </div>
        <DialogFooter><Button data-testid="person-save" onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">{saving ? "Saving" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Directory({ data, onChanged }) {
  return (
    <Panel
      title="Team directory" testId="team-directory"
      action={data.can_edit ? (
        <PersonDialog onSaved={onChanged}
          trigger={<button data-testid="add-person-button" className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-[#5b78e8]"><Plus className="h-3.5 w-3.5" strokeWidth={2} /> Add member</button>} />
      ) : null}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground border-b border-border">
            {th("Name")}{th("Title")}{th("Role")}{th("Location")}{th("Contact")}{th("Equity", "text-right")}{data.can_edit && th("", "text-right")}
          </tr></thead>
          <tbody className="divide-y divide-border">
            {data.people.map((p) => (
              <tr key={p.id} data-testid="person-row" className="hover:bg-white/[0.03]">
                <td className="px-4 py-3 text-foreground">{p.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{p.role_title || EMPTY_MARK}</td>
                <td className="px-4 py-3"><StatusPill dot={false} tone="accent" label={ROLE_LABELS[p.role] || p.role} /></td>
                <td className="px-4 py-3 text-muted-foreground">{p.location || EMPTY_MARK}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{p.email || EMPTY_MARK}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-foreground">{pct(p.equity_pct)}</td>
                {data.can_edit && (
                  <td className="px-4 py-3 text-right">
                    <PersonDialog person={p} onSaved={onChanged}
                      trigger={<button data-testid={`person-edit-${p.id}`} className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" strokeWidth={1.75} /></button>} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ---- Cap table ---------------------------------------------------------------
function CapEntryDialog({ entry, onSaved, trigger }) {
  const editing = !!entry;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(entry || { holder: "", instrument: "Equity", units: 0, percentage: 0, is_option_pool: false });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.holder.trim()) { toast.error("Holder is required"); return; }
    setSaving(true);
    try {
      const payload = { ...f, units: parseFloat(f.units) || 0, percentage: parseFloat(f.percentage) || 0 };
      if (editing) await api.patch(`/people/cap-table/${entry.id}`, payload);
      else await api.post("/people/cap-table", payload);
      toast.success(editing ? "Entry updated" : "Entry added");
      setOpen(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-lg">{editing ? "Edit holder" : "Add holder"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Holder</Label>
            <Input data-testid="cap-holder-input" value={f.holder} onChange={(e) => set("holder", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Instrument</Label>
              <Select value={f.instrument} onValueChange={(v) => set("instrument", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Equity", "Options", "SAFE", "Convertible note"].map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Units</Label>
              <Input type="number" value={f.units ?? 0} onChange={(e) => set("units", e.target.value)} className="font-mono" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Percentage</Label>
              <Input type="number" value={f.percentage ?? 0} onChange={(e) => set("percentage", e.target.value)} className="font-mono" /></div>
          </div>
        </div>
        <DialogFooter><Button data-testid="cap-save" onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">{saving ? "Saving" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CapTable({ data, onChanged }) {
  const d = data.dilution;
  return (
    <div className="grid grid-cols-12 gap-5">
      <div className="col-span-12 xl:col-span-7">
        <Panel
          title="Cap table" testId="cap-table"
          action={data.can_edit ? (
            <CapEntryDialog onSaved={onChanged}
              trigger={<button data-testid="add-cap-entry-button" className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-[#5b78e8]"><Plus className="h-3.5 w-3.5" strokeWidth={2} /> Add holder</button>} />
          ) : <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Read only</span>}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground border-b border-border">
                {th("Holder")}{th("Instrument")}{th("Units", "text-right")}{th("Percentage", "text-right")}{data.can_edit && th("", "text-right")}
              </tr></thead>
              <tbody className="divide-y divide-border">
                {data.entries.map((e) => (
                  <tr key={e.id} data-testid="cap-row" className="hover:bg-white/[0.03]">
                    <td className="px-4 py-3 text-foreground">{e.holder}{e.is_option_pool && <span className="ml-2 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">pool</span>}</td>
                    <td className="px-4 py-3 text-muted-foreground">{e.instrument}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-muted-foreground">{Number(e.units).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-foreground">{pct(e.percentage, 2)}</td>
                    {data.can_edit && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-3">
                          <CapEntryDialog entry={e} onSaved={onChanged}
                            trigger={<button data-testid={`cap-edit-${e.id}`} className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" strokeWidth={1.75} /></button>} />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button data-testid={`cap-delete-${e.id}`} className="text-muted-foreground hover:text-[#EF4444]"><Trash2 className="h-4 w-4" strokeWidth={1.75} /></button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Remove {e.holder}?</AlertDialogTitle>
                                <AlertDialogDescription>This removes the holder from the cap table.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction className="bg-[#b91c1c] hover:bg-[#a01818]" onClick={async () => {
                                  try { await api.delete(`/people/cap-table/${e.id}`); toast.success("Removed"); onChanged(); } catch { toast.error("Could not remove"); }
                                }}>Remove</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                <tr className="border-t border-border bg-white/[0.02]">
                  <td className="px-4 py-3 text-muted-foreground uppercase text-[11px] tracking-[0.1em]" colSpan={3}>Total</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-foreground">{pct(data.total_pct, 2)}</td>
                  {data.can_edit && <td />}
                </tr>
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
      <div className="col-span-12 xl:col-span-5">
        <Panel title="Dilution against the raise" testId="dilution-panel">
          {!d ? <EmptyState message="No raise configured for modelling." /> : (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <div><SectionLabel>Pre-money</SectionLabel><p className="mt-1 font-mono tabular-nums text-sm text-foreground">{moneyFull(d.pre_money_valuation, d.currency)}</p></div>
                <div><SectionLabel>New money</SectionLabel><p className="mt-1 font-mono tabular-nums text-sm text-foreground">{moneyFull(d.new_money, d.currency)}</p></div>
                <div><SectionLabel>Post-money</SectionLabel><p className="mt-1 font-mono tabular-nums text-sm text-foreground">{moneyFull(d.post_money_valuation, d.currency)}</p></div>
                <div><SectionLabel>New investor stake</SectionLabel><p className="mt-1 font-mono tabular-nums text-sm text-foreground">{pct(d.new_investor_pct, 2)}</p></div>
              </div>
              <div className="border-t border-border pt-3">
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 text-[11px] uppercase tracking-[0.1em] text-muted-foreground pb-2">
                  <span>Holder</span><span className="text-right">Pre</span><span className="text-right">Post</span>
                </div>
                <div className="space-y-1.5">
                  {d.rows.map((r, i) => (
                    <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-x-4 text-sm">
                      <span className="text-foreground truncate">{r.holder}</span>
                      <span className="text-right font-mono tabular-nums text-muted-foreground">{pct(r.pre_pct, 1)}</span>
                      <span className="text-right font-mono tabular-nums text-foreground">{pct(r.post_pct, 1)}</span>
                    </div>
                  ))}
                </div>
              </div>
              {d.is_placeholder && <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Placeholder model</p>}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

// ---- Work passes -------------------------------------------------------------
function WorkPassDialog({ pass, people, onSaved, trigger }) {
  const editing = !!pass;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(pass || { person_id: "", pass_type: "", route: "", status: "Active", issued_on: "", expires_on: "", notes: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!editing && !f.person_id) { toast.error("Select a person"); return; }
    setSaving(true);
    try {
      const payload = { ...f, issued_on: f.issued_on || null, expires_on: f.expires_on || null };
      if (editing) await api.patch(`/people/work-passes/${pass.id}`, payload);
      else await api.post("/people/work-passes", payload);
      toast.success(editing ? "Work pass updated" : "Work pass added");
      setOpen(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-lg">{editing ? "Edit work pass" : "Add work pass"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!editing && (
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Person</Label>
              <Select value={f.person_id} onValueChange={(v) => set("person_id", v)}>
                <SelectTrigger data-testid="wp-person-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Type</Label>
              <Input value={f.pass_type} onChange={(e) => set("pass_type", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Route</Label>
              <Input value={f.route} onChange={(e) => set("route", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PASS_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div />
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Issued on</Label>
              <Input type="date" value={(f.issued_on || "").slice(0, 10)} onChange={(e) => set("issued_on", e.target.value)} className="font-mono" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-muted-foreground">Expires on</Label>
              <Input type="date" value={(f.expires_on || "").slice(0, 10)} onChange={(e) => set("expires_on", e.target.value)} className="font-mono" /></div>
          </div>
        </div>
        <DialogFooter><Button data-testid="wp-save" onClick={save} disabled={saving} className="bg-primary hover:bg-[#2746c0]">{saving ? "Saving" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WorkPasses({ data, people, onChanged }) {
  return (
    <Panel
      title="Work-pass tracker" testId="work-passes"
      action={data.can_edit ? (
        <WorkPassDialog people={people} onSaved={onChanged}
          trigger={<button data-testid="add-workpass-button" className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-[#5b78e8]"><Plus className="h-3.5 w-3.5" strokeWidth={2} /> Add work pass</button>} />
      ) : null}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground border-b border-border">
            {th("Person")}{th("Type")}{th("Route")}{th("Status")}{th("Issued")}{th("Expires")}{th("Reminder")}{data.can_edit && th("", "text-right")}
          </tr></thead>
          <tbody className="divide-y divide-border">
            {data.work_passes.map((w) => (
              <tr key={w.id} data-testid="workpass-row" className="hover:bg-white/[0.03]">
                <td className="px-4 py-3 text-foreground">{w.person_name || EMPTY_MARK}</td>
                <td className="px-4 py-3 text-muted-foreground">{w.pass_type || EMPTY_MARK}</td>
                <td className="px-4 py-3 text-muted-foreground">{w.route || EMPTY_MARK}</td>
                <td className="px-4 py-3"><StatusPill dot={false} tone={w.status === "Active" ? "on_track" : w.status === "Expired" ? "blocked" : w.status === "Expiring" ? "at_risk" : "neutral"} label={w.status} /></td>
                <td className="px-4 py-3 font-mono text-muted-foreground">{w.issued_on ? shortDay(w.issued_on) : EMPTY_MARK}</td>
                <td className="px-4 py-3 font-mono text-muted-foreground">{w.expires_on ? shortDay(w.expires_on) : EMPTY_MARK}</td>
                <td className="px-4 py-3">
                  {w.reminder ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-[#FFD79A]"><AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.75} />{w.days_to_expiry}d to expiry</span>
                  ) : <span className="text-muted-foreground/60">{EMPTY_MARK}</span>}
                </td>
                {data.can_edit && (
                  <td className="px-4 py-3 text-right">
                    <WorkPassDialog pass={w} people={people} onSaved={onChanged}
                      trigger={<button data-testid={`workpass-edit-${w.id}`} className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" strokeWidth={1.75} /></button>} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ---- Responsibilities --------------------------------------------------------
function Responsibilities({ rows }) {
  return (
    <Panel title="Roles and responsibilities" testId="responsibilities">
      {rows.length === 0 ? <EmptyState message="No responsibilities recorded." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground border-b border-border">
              {th("Area")}{th("Scope")}{th("Owner")}{th("Title")}{th("Arm")}
            </tr></thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} data-testid="responsibility-row" className="hover:bg-white/[0.03]">
                  <td className="px-4 py-3 text-foreground">{r.area}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.scope}</td>
                  <td className="px-4 py-3 text-foreground">{r.person_name || EMPTY_MARK}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.role_title || EMPTY_MARK}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.arm || EMPTY_MARK}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

// ---- Access (Owner only) -----------------------------------------------------
function Access({ data, onChanged }) {
  return (
    <Panel title="Access and roles" testId="access-panel">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-[0.1em] text-muted-foreground border-b border-border">
            {th("User")}{th("Email")}{th("Role")}
          </tr></thead>
          <tbody className="divide-y divide-border">
            {data.users.map((u) => (
              <tr key={u.id} data-testid="user-row" className="hover:bg-white/[0.03]">
                <td className="px-4 py-3 text-foreground">{u.name}</td>
                <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={async (v) => {
                    try { await api.patch(`/people/users/${u.id}/role`, { role: v }); toast.success("Role updated"); onChanged(); }
                    catch (e) { toast.error(e?.response?.data?.detail || "Could not update role"); }
                  }}>
                    <SelectTrigger data-testid={`user-role-${u.id}`} className="w-48"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ---- Page --------------------------------------------------------------------
export default function PeopleOrg() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const canFinance = user?.role === "owner" || user?.role === "founder";

  const [directory, setDirectory] = useState(null);
  const [cap, setCap] = useState(null);
  const [passes, setPasses] = useState(null);
  const [resp, setResp] = useState([]);
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restricted, setRestricted] = useState(false);

  const loadDirectory = useCallback(async () => setDirectory((await api.get("/people")).data), []);
  const loadCap = useCallback(async () => setCap((await api.get("/people/cap-table")).data), []);
  const loadPasses = useCallback(async () => setPasses((await api.get("/people/work-passes")).data), []);
  const loadUsers = useCallback(async () => setUsers((await api.get("/people/users")).data), []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [api.get("/people"), api.get("/people/work-passes"), api.get("/people/responsibilities")];
      const [dir, wp, rp] = await Promise.all(calls);
      setDirectory(dir.data); setPasses(wp.data); setResp(rp.data);
      if (canFinance) { try { setCap((await api.get("/people/cap-table")).data); } catch (e) { /* gated */ } }
      if (isOwner) { try { setUsers((await api.get("/people/users")).data); } catch (e) { /* gated */ } }
    } catch (e) {
      if (e?.response?.status === 403) setRestricted(true);
      else toast.error("Could not load People and Org.");
    } finally { setLoading(false); }
  }, [canFinance, isOwner]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading) return <Loading label="Loading people and org" />;
  if (restricted) {
    return (
      <div className="rounded-md border border-border bg-card px-6 py-16 text-center">
        <Lock className="mx-auto h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
        <h2 className="mt-4 font-display text-xl text-foreground">People and Org is restricted</h2>
        <p className="mt-2 text-sm text-muted-foreground">This area is available to the studio team.</p>
      </div>
    );
  }
  const people = directory?.people || [];

  return (
    <Tabs defaultValue="directory" className="space-y-5">
      <TabsList className="bg-card border border-border">
        <TabsTrigger value="directory" data-testid="tab-directory">Directory</TabsTrigger>
        {canFinance && <TabsTrigger value="captable" data-testid="tab-captable">Cap table</TabsTrigger>}
        <TabsTrigger value="passes" data-testid="tab-passes">Work passes</TabsTrigger>
        <TabsTrigger value="resp" data-testid="tab-resp">Responsibilities</TabsTrigger>
        {isOwner && <TabsTrigger value="access" data-testid="tab-access">Access</TabsTrigger>}
      </TabsList>

      <TabsContent value="directory">{directory && <Directory data={directory} onChanged={loadDirectory} />}</TabsContent>
      {canFinance && <TabsContent value="captable">{cap && <CapTable data={cap} onChanged={loadCap} />}</TabsContent>}
      <TabsContent value="passes">{passes && <WorkPasses data={passes} people={people} onChanged={loadPasses} />}</TabsContent>
      <TabsContent value="resp"><Responsibilities rows={resp} /></TabsContent>
      {isOwner && <TabsContent value="access">{users && <Access data={users} onChanged={loadUsers} />}</TabsContent>}
    </Tabs>
  );
}
